import { useMemo, useState } from 'react';
import Pagination from '../ui/Pagination';
import { libelleMetier } from '../../config/servicesCatalog';
import {
  PROSPECT_STATUS_BADGES,
  PROSPECT_STATUS_LABELS
} from '../../services/prospectService';

/**
 * Onglet « Tous » (§5.5) : tableau filtrable, triable côté client, paginé à 25 lignes via
 * `Pagination.js` (signature existante `{ currentPage, totalPages, onPageChange }`).
 */

const PAR_PAGE = 25;

const COLONNES = [
  { cle: 'score', label: 'Score', triable: true },
  { cle: 'name', label: 'Nom', triable: true },
  { cle: 'primaryCategory', label: 'Métier', triable: true },
  { cle: 'ville', label: 'Ville', triable: true },
  { cle: 'status', label: 'Statut', triable: true },
  { cle: 'lastCallAt', label: 'Dernier appel', triable: true },
  { cle: 'nextCallAt', label: 'Prochain appel', triable: true }
];

export const formaterDate = (valeur) => {
  if (!valeur) return '—';
  const date = valeur instanceof Date ? valeur : new Date(valeur);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const valeurDeTri = (prospect, cle) => {
  switch (cle) {
    case 'ville':
      return prospect.address?.city || '';
    case 'primaryCategory':
      // Même valeur que la cellule affichée : catégorie Google, sinon libellé du métier
      return String(
        prospect.primaryCategory || libelleMetier(prospect.targetTrade) || ''
      ).toLowerCase();
    case 'lastCallAt':
    case 'nextCallAt':
      return prospect[cle] instanceof Date ? prospect[cle].getTime() : 0;
    case 'score':
      return Number(prospect.score) || 0;
    default:
      return String(prospect[cle] || '').toLowerCase();
  }
};

const ProspectsList = ({ prospects = [], onOuvrir }) => {
  const [tri, setTri] = useState({ cle: 'score', sens: 'desc' });
  const [page, setPage] = useState(1);

  const triees = useMemo(() => {
    const copie = [...prospects];
    copie.sort((a, b) => {
      const va = valeurDeTri(a, tri.cle);
      const vb = valeurDeTri(b, tri.cle);
      if (va === vb) return 0;
      const ordre = va > vb ? 1 : -1;
      return tri.sens === 'asc' ? ordre : -ordre;
    });
    return copie;
  }, [prospects, tri]);

  const totalPages = Math.max(1, Math.ceil(triees.length / PAR_PAGE));
  const pageCourante = Math.min(page, totalPages);
  const lignes = triees.slice((pageCourante - 1) * PAR_PAGE, pageCourante * PAR_PAGE);

  const changerTri = (cle) => {
    setTri((ancien) =>
      ancien.cle === cle ? { cle, sens: ancien.sens === 'asc' ? 'desc' : 'asc' } : { cle, sens: 'asc' }
    );
    setPage(1);
  };

  if (prospects.length === 0) {
    return (
      <div className="text-center py-12 text-base-content/60">
        <p className="text-lg mb-2">Aucun prospect</p>
        <p className="text-sm">Commencez par importer un fichier Apify.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="table table-sm">
          <thead>
            <tr>
              {COLONNES.map((colonne) => (
                <th
                  key={colonne.cle}
                  className="cursor-pointer select-none whitespace-nowrap"
                  onClick={() => changerTri(colonne.cle)}
                >
                  {colonne.label}
                  {tri.cle === colonne.cle && (tri.sens === 'asc' ? ' ▲' : ' ▼')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lignes.map((prospect) => (
              <tr
                key={prospect.placeId}
                className="hover cursor-pointer"
                onClick={() => onOuvrir(prospect.placeId)}
              >
                <td className="font-bold text-primary">{prospect.score ?? '—'}</td>
                <td className="max-w-[240px] truncate">{prospect.name}</td>
                <td>
                  {prospect.primaryCategory || libelleMetier(prospect.targetTrade) || '—'}
                </td>
                <td>
                  {prospect.address?.city || '—'}
                  {prospect.departmentCode ? ` (${prospect.departmentCode})` : ''}
                </td>
                <td>
                  <span
                    className={`badge badge-sm ${
                      PROSPECT_STATUS_BADGES[prospect.status] || 'badge-ghost'
                    }`}
                  >
                    {PROSPECT_STATUS_LABELS[prospect.status] || prospect.status}
                  </span>
                </td>
                <td>{formaterDate(prospect.lastCallAt)}</td>
                <td>{formaterDate(prospect.nextCallAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
        <span className="text-sm text-base-content/60">
          {prospects.length} fiche{prospects.length > 1 ? 's' : ''} · page {pageCourante} /{' '}
          {totalPages}
        </span>
        {totalPages > 1 && (
          <Pagination currentPage={pageCourante} totalPages={totalPages} onPageChange={setPage} />
        )}
      </div>
    </div>
  );
};

export default ProspectsList;
